/**
 * Frontend production feature flags.
 * Mirrors backend `production-flags.ts`.
 *
 * Demo Workspace is a core feature controlled by VITE_FEATURE_DEMO_MODE /
 * hydrated FEATURE_DEMO_MODE from GET /feature-flags/public.
 * GPS / routes remain hard-off in production builds.
 */

export type ClientFeatureFlags = {
  demoMode: boolean;
  gpsLiveTracking: boolean;
  routesPlanner: boolean;
  notificationsInApp: boolean;
  notificationsEmail: boolean;
  paymentGateway: boolean;
  requireVerifiedPodForInvoice: boolean;
};

const truthy = (v: unknown) => v === true || v === 'true' || v === '1';

function envFlag(key: string, fallback: boolean): boolean {
  const raw = (import.meta as ImportMeta & { env: Record<string, string> }).env[key];
  if (raw === undefined || raw === '') return fallback;
  return truthy(raw);
}

/** Synchronous defaults from Vite env (used before / before API hydrate). */
export function getClientFeatureFlags(): ClientFeatureFlags {
  const isProd = import.meta.env.PROD === true || import.meta.env.MODE === 'production';

  const demoRequested = envFlag('VITE_FEATURE_DEMO_MODE', !isProd);
  const gpsRequested = envFlag('VITE_FEATURE_GPS_LIVE', false);
  const routesRequested = envFlag('VITE_FEATURE_ROUTES_PLANNER', false);

  return {
    // Demo is flag-controlled (core sales/evaluation workspace).
    demoMode: demoRequested,
    gpsLiveTracking: isProd ? false : gpsRequested,
    routesPlanner: isProd ? false : routesRequested,
    notificationsInApp: envFlag('VITE_FEATURE_NOTIFICATIONS_IN_APP', true),
    notificationsEmail: envFlag('VITE_FEATURE_NOTIFICATIONS_EMAIL', true),
    paymentGateway: envFlag('VITE_FEATURE_PAYMENT_GATEWAY', false),
    requireVerifiedPodForInvoice: envFlag('VITE_FEATURE_REQUIRE_VERIFIED_POD', false),
  };
}

let hydrated: ClientFeatureFlags | null = null;

export function getHydratedFeatureFlags(): ClientFeatureFlags {
  return hydrated ?? getClientFeatureFlags();
}

export function setHydratedFeatureFlags(flags: Partial<ClientFeatureFlags>): void {
  hydrated = { ...getClientFeatureFlags(), ...flags };
  // Production hard locks for quarantined surfaces only (not Demo Workspace).
  if (import.meta.env.PROD) {
    hydrated.gpsLiveTracking = false;
    hydrated.routesPlanner = false;
  }
}

export function isDemoFeatureAllowed(): boolean {
  return getHydratedFeatureFlags().demoMode;
}

export function isGpsLiveAllowed(): boolean {
  return getHydratedFeatureFlags().gpsLiveTracking;
}

export function isRoutesPlannerAllowed(): boolean {
  return getHydratedFeatureFlags().routesPlanner;
}

export function isNotificationsUiAllowed(): boolean {
  return getHydratedFeatureFlags().notificationsInApp;
}
